import { useState, useEffect } from 'react';
import { useSelector } from 'react-redux';
import { useApolloClient, gql } from '@apollo/client';
import {
	CALL_SNAPPY_API,
	CALL_SNAPPY_API_MUTATE,
} from 'src/rewardsStore/RewardsStoreGraphQL';
import { Link } from 'react-router-dom';
import dayjs from 'dayjs';

import { motion } from 'motion/react';
import {
	Typography,
	Table,
	Tag,
	Spin,
	Select,
	Switch,
	Popconfirm,
	Popover,
	Button,
	message,
	Tooltip,
	DatePicker,
} from 'antd';
import {
	StopOutlined,
	CopyOutlined,
	InfoCircleOutlined,
} from '@ant-design/icons';

const { Title } = Typography;
const { RangePicker } = DatePicker;

const QUERY_SNAPPY_GIFTS_BY_EXTERNAL_IDS = gql`
	query querySnappyGiftsByExternalIds($externalIds: [ID]) {
		querySnappyGiftsByExternalIds(externalIds: $externalIds) {
			id
			firstSeenAt
			badgeAssignmentId
			companyId
			createdAt
			externalSnappyGiftId
			link
			snappyCampaignId
			snappyCampaignTierId
			snappyGrantId
			userId
			badgeAssignment {
				id
				createdAt
				recipients {
					userId
					user {
						firstName
						lastName
						emailAddress
						id
					}
					accountClaim {
						id
						lastName
						firstName
						emailAddress
					}
				}
				badgeId
				badge {
					id
					name
					snappyGrant {
						id
						name
					}
				}
			}
			user {
				id
				firstName
				emailAddress
				lastName
			}
		}
	}
`;

const SnappyHistoryComponent = (props) => {
	const currentUser = useSelector((state) => state.user.currentUser);
	const companyId = currentUser?.company?.id;
	const client = useApolloClient();
	const [pageSize, setPageSize] = useState(15);
	const [pageIndex, setPageIndex] = useState(0);
	const [displayPageIndex, setDisplayPageIndex] = useState(0); // The page actually displayed in the table
	const [campaignOptions, setCampaignOptions] = useState([]);
	const [selectedCampaigns, setSelectedCampaigns] = useState([]);
	const [showAllCampaigns, setShowAllCampaigns] = useState(true);
	const [dateRange, setDateRange] = useState(null);
	const [hasNextPage, setHasNextPage] = useState(false);
	const [knownTotalPages, setKnownTotalPages] = useState(1); // Track the highest known page count

	const [initialLoading, setInitialLoading] = useState(false);
	const [loadingMore, setLoadingMore] = useState(false);
	const [snappyGiftData, setSnappyGiftData] = useState([]);
	const [fetchedPages, setFetchedPages] = useState(new Set());
	const [expiringGiftId, setExpiringGiftId] = useState(null);

	useEffect(() => {
		fetchCampaigns();
	}, []);

	useEffect(() => {
		if (companyId && selectedCampaigns.length > 0) {
			fetchSnappyGiftData();
		}
	}, [companyId, selectedCampaigns, dateRange]);

	useEffect(() => {
		if (
			companyId &&
			pageIndex > 0 &&
			!fetchedPages.has(pageIndex) &&
			selectedCampaigns.length > 0
		) {
			fetchSnappyGiftData();
		}
	}, [pageIndex, companyId]);

	const sort = (a = '', b = '') => {
		if (!a) return -1;
		if (!b) return 1;
		if (typeof a === 'string') {
			a = a.toLowerCase();
		}

		if (typeof b === 'string') {
			b = b.toLowerCase();
		}

		if (a > b) return 1;
		if (a < b) return -1;
		return 0;
	};

	const capitalize = (input) => {
		if (!input || !input?.length > 1) return input;
		const firstLetter = input?.charAt(0)?.toUpperCase();
		return firstLetter + input?.slice(1);
	};

	const fetchCampaigns = async () => {
		setInitialLoading(true);
		try {
			const { data } = await client.query({
				query: CALL_SNAPPY_API,
				variables: {
					companyId: currentUser.companyId,
					endpoint: 'campaigns',
					method: 'GET',
					queryParams: [
						{ key: 'types', value: 'oneOffs' },
						{ key: 'fields', value: 'id,name,properties' },
					],
				},
				fetchPolicy: 'network-only',
			});

			if (data?.callSnappyApi) {
				try {
					const parsedResponse = JSON.parse(data.callSnappyApi);
					const results = parsedResponse?.results || [];
					if (results && Array.isArray(results)) {
						const campaignOptions = results.map((result) => ({
							label: result.name,
							value: result.id,
						}));
						setCampaignOptions(campaignOptions);
						setSelectedCampaigns(campaignOptions.map((c) => c.value));
						return campaignOptions || [];
					} else {
						setCampaignOptions([]);
						setSelectedCampaigns([]);
						setInitialLoading(false);
						return [];
					}
				} catch (parseError) {
					console.error('Error parsing campaigns response:', parseError);
					setCampaignOptions([]);
					setSelectedCampaigns([]);
					setInitialLoading(false);
					return [];
				}
			} else {
				setCampaignOptions([]);
				setSelectedCampaigns([]);
				setInitialLoading(false);
				return [];
			}
		} catch (error) {
			console.error('Error fetching campaigns:', error);
			setCampaignOptions([]);
			setSelectedCampaigns([]);
			setInitialLoading(false);
			return [];
		}
	};

	const expireGift = async (giftId) => {
		try {
			const { data } = await client.mutate({
				mutation: CALL_SNAPPY_API_MUTATE,
				variables: {
					companyId: companyId,
					endpoint: `gifts/${giftId}/expire`,
					method: 'POST',
					queryParams: [
						{
							key: 'companyId',
							value: true,
						},
					],
				},
			});

			if (data?.callSnappyApiMutate) {
				try {
					const parsedResponse = JSON.parse(data.callSnappyApiMutate);
					const status = parsedResponse?.status || '';
					if (status === 'success') {
						return status;
					} else {
						console.error('Unable to expire gift');
						message.error(`Gift (ID: '${giftId}') was unable to be expired.`);
						return null;
					}
				} catch (parseError) {
					console.error(
						`Error parsing expire gift response for giftId ${giftId}:`,
						parseError
					);
					return null;
				}
			}
		} catch (error) {
			console.error(`Error expiring gift with giftId of ${giftId}:`, error);
			return null;
		}
	};

	const querySnappyGiftsByExternalIds = async (externalIds) => {
		try {
			const { data } = await client.query({
				query: QUERY_SNAPPY_GIFTS_BY_EXTERNAL_IDS,
				variables: {
					externalIds: externalIds,
				},
			});

			if (data?.querySnappyGiftsByExternalIds) {
				return data.querySnappyGiftsByExternalIds || [];
			}
		} catch (error) {
			console.error(`Error retrieving internal gift data:`, error);
			return [];
		}
	};

	const handleExpireGift = async (giftId) => {
		setExpiringGiftId(giftId);
		try {
			const status = await expireGift(giftId);
			if (status === 'success') {
				setSnappyGiftData((prevData) =>
					prevData.map((gift) =>
						gift.id === giftId ? { ...gift, status: 'expired' } : gift
					)
				);
				message.success(`Gift (ID: '${giftId}') was successfully expired.`);
			}
		} finally {
			setExpiringGiftId(null);
		}
	};

	const fetchSnappyGiftData = async () => {
		if (
			companyId &&
			!fetchedPages.has(pageIndex) &&
			selectedCampaigns.length > 0
		) {
			setLoadingMore(true);
			try {
				const queryParams = [
					{
						key: 'campaignIds',
						value: selectedCampaigns.join(','),
					},
					{
						key: 'fields',
						value:
							'id,status,budgetPlan,tyn,expirationDate,createdAt,link,campaignId',
					},
					{ key: 'expand', value: 'recipient,orders,finalCost' },
					{
						key: 'statuses',
						value: 'unopened,opened,unwrapped,claimed,expired',
					},
					{ key: 'skip', value: pageIndex * pageSize },
					{ key: 'limit', value: pageSize + 1 },
				];

				if (dateRange && dateRange[0] && dateRange[1]) {
					const fromDate = dateRange[0].subtract(1, 'day').format('YYYY-MM-DD');
					const toDate = dateRange[1].add(1, 'day').format('YYYY-MM-DD');
					queryParams.push(
						{ key: 'createdAfter', value: fromDate },
						{ key: 'createdBefore', value: toDate }
					);
				}

				const { data } = await client.query({
					query: CALL_SNAPPY_API,
					variables: {
						companyId: companyId,
						endpoint: 'gifts',
						method: 'GET',
						queryParams: queryParams,
					},
					fetchPolicy: 'network-only',
				});
				if (data?.callSnappyApi) {
					try {
						const parsedResponse = JSON.parse(data.callSnappyApi);
						const results = parsedResponse?.results || [];

						const externalIds = results.map((result) => result.id);
						const internalGiftData =
							await querySnappyGiftsByExternalIds(externalIds);

						const resultsWithInternalData = results.map((result) => {
							const matchingInternalGift = internalGiftData.find(
								(internalGift) =>
									internalGift.externalSnappyGiftId === result.id
							);
							return {
								...result,
								internalGiftData: matchingInternalGift || null,
							};
						});

						const hasMore = resultsWithInternalData.length > pageSize;
						setHasNextPage(hasMore);

						const itemsToAdd =
							pageIndex > 0
								? resultsWithInternalData.slice(1)
								: resultsWithInternalData;

						setSnappyGiftData((prevData) => [...prevData, ...itemsToAdd]);
						setFetchedPages((prev) => new Set([...prev, pageIndex]));

						const currentKnownPages = pageIndex + 1;
						const totalKnownPages = hasMore
							? currentKnownPages + 1
							: currentKnownPages;
						setKnownTotalPages(Math.max(knownTotalPages, totalKnownPages));

						// Now that data is loaded, update the displayed page
						setDisplayPageIndex(pageIndex);
					} catch (parseError) {
						console.error('Error parsing gifts response:', parseError);
						if (pageIndex === 0) {
							setSnappyGiftData([]);
						}
						setHasNextPage(false);
					}
				}
			} catch (error) {
				console.error('Error fetching gift data:', error);
			} finally {
				setLoadingMore(false);
				if (pageIndex === 0) {
					setInitialLoading(false);
				}
			}
		}
	};

	const handleCampaignChange = (values) => {
		setSelectedCampaigns(values);
		setPageIndex(0);
		setDisplayPageIndex(0);
		setSnappyGiftData([]);
		setFetchedPages(new Set());
		setHasNextPage(false);
		setKnownTotalPages(1);
	};

	const handleShowAllCampaignsToggle = (checked) => {
		setShowAllCampaigns(checked);
		if (checked) {
			setSelectedCampaigns(campaignOptions.map((c) => c.value));
		} else {
			setSelectedCampaigns([]);
		}
		setPageIndex(0);
		setDisplayPageIndex(0);
		setSnappyGiftData([]);
		setFetchedPages(new Set());
		setHasNextPage(false);
		setKnownTotalPages(1);
	};

	const handleDateRangeChange = (dates) => {
		setDateRange(dates);
		setPageIndex(0);
		setDisplayPageIndex(0);
		setSnappyGiftData([]);
		setFetchedPages(new Set());
		setHasNextPage(false);
		setKnownTotalPages(1);
	};

	const snappyHistoryColumns = [
		{
			title: 'Date',
			dataIndex: 'createdAt',
			key: 'createdAt',
			defaultSortOrder: 'ascend',
			sorter: (a, b) => dayjs(a.createdAt).unix() - dayjs(b.createdAt).unix(),
			render: (createdAt) => {
				return dayjs(createdAt).format('MM/DD/YYYY');
			},
		},
		{
			title: 'Recipient',
			dataIndex: 'recipient',
			key: 'recipient',
			sorter: (a, b) =>
				sort(
					a?.orders?.[0]?.orderRecipient?.firstName || a?.recipient?.firstname,
					b?.orders?.[0]?.orderRecipient?.firstName || b?.recipient?.firstname
				),
			render: (_, record) => {
				const internalUser = record?.internalGiftData?.user;
				const userFullName =
					`${internalUser?.firstName || ''} ${internalUser?.lastName || ''}`.trim();
				const profileLink = (
					<Link to={`/employees/${internalUser?.id}`}>{userFullName}</Link>
				);
				const recipients =
					record?.internalGiftData?.badgeAssignment?.recipients ||
					(record?.internalGiftData?.user
						? [{ user: record.internalGiftData.user }]
						: []);
				const mappedRecipients = recipients.map((recipient) => {
					if (recipient.user) {
						return {
							id: recipient.user?.id,
							firstName: recipient.user?.firstName,
							lastName: recipient.user?.lastName,
							emailAddress: recipient.user?.emailAddress,
						};
					} else if (recipient.accountClaim) {
						return {
							id: 'account_claim',
							firstName: recipient.accountClaim?.firstName,
							lastName: recipient.accountClaim?.lastName,
							emailAddress: recipient.accountClaim?.emailAddress,
						};
					}
				});
				const originalRecipient = `${record?.recipient?.firstname} ${record?.recipient?.lastname}`;
				const orderRecipient =
					`${record?.orders?.[0]?.orderRecipient?.firstName || ''} ${record?.orders?.[0]?.orderRecipient?.lastName || ''}`.trim();

				const withPopover = (
					<Popover
						content={
							<div
								style={{
									maxWidth: 400,
									maxHeight: 300,
									overflowY: 'scroll',
									marginBottom: 0,
								}}
							>
								{!record?.internalGiftData ? (
									<span
										style={{
											fontWeight: 800,
											color: 'rgba(0, 0, 0, 0.85)',
										}}
									>
										This gift was created outside of ERIN.
									</span>
								) : (
									<>
										<span
											style={{
												fontWeight: 800,
												color: 'rgba(0, 0, 0, 0.85)',
											}}
										>
											Full Recognition Recipients:
										</span>
										<ul style={{ marginBottom: 0 }}>
											{mappedRecipients.length > 0 && (
												<>
													{mappedRecipients.map((re) => {
														const recipientString = `${re.firstName} ${re.lastName} - ${re.emailAddress}`;
														const matchingId =
															re.id === record.internalGiftData.userId;
														return (
															<li
																key={re.id}
																style={matchingId ? { fontWeight: 800 } : {}}
															>
																{recipientString}
															</li>
														);
													})}
												</>
											)}
										</ul>
									</>
								)}
							</div>
						}
						getPopupContainer={(triggerNode) => triggerNode.parentNode}
					>
						<span
							style={{
								fontSize: '14px',
								color: 'var(--lochmara)',
								cursor: 'help',
							}}
						>
							<span
								style={{
									fontSize: '14px',
									color: 'var(--lochmara)',
									cursor: 'help',
									marginLeft: '8px',
								}}
							>
								<InfoCircleOutlined />
							</span>
						</span>
					</Popover>
				);
				return (
					<div style={{ display: 'flex', alignItems: 'center' }}>
						{userFullName ? (
							profileLink
						) : (
							<span>{orderRecipient || originalRecipient}</span>
						)}
						{withPopover}
					</div>
				);
			},
		},
		{
			title: 'Recognition',
			dataIndex: 'internalGiftData',
			key: 'internalGiftData',
			render: (internalGiftData) => {
				if (!internalGiftData) return <span>–</span>;
				const assignmentId = internalGiftData?.badgeAssignment?.id;
				const badgeId = internalGiftData?.badgeAssignment?.badgeId;
				const badgeName = internalGiftData?.badgeAssignment?.badge?.name;
				const snappyGrantName =
					internalGiftData?.badgeAssignment?.badge?.snappyGrant?.name;
				const recipients =
					internalGiftData?.badgeAssignment?.recipients ||
					(internalGiftData?.user ? [{ user: internalGiftData.user }] : []);
				const mappedRecipients = recipients.map((recipient) => {
					if (recipient.user) {
						return {
							id: recipient.user?.id,
							firstName: recipient.user?.firstName,
							lastName: recipient.user?.lastName,
							emailAddress: recipient.user?.emailAddress,
						};
					} else if (recipient.accountClaim) {
						return {
							id: 'account_claim',
							firstName: recipient.accountClaim?.firstName,
							lastName: recipient.accountClaim?.lastName,
							emailAddress: recipient.accountClaim?.emailAddress,
						};
					}
				});
				const withPopover = (
					<Popover
						content={
							<div
								style={{
									maxWidth: 200,
									maxHeight: 300,
									overflowY: 'scroll',
									marginBottom: 0,
								}}
							>
								<ul style={{ marginBottom: 0 }}>
									{assignmentId && (
										<li>
											<span
												style={{
													fontWeight: 800,
													color: 'rgba(0, 0, 0, 0.85)',
												}}
											>
												Assignment ID
											</span>
											{`: ${assignmentId}`}
										</li>
									)}
									{badgeId && (
										<li>
											<span
												style={{
													fontWeight: 800,
													color: 'rgba(0, 0, 0, 0.85)',
												}}
											>
												Recognition ID
											</span>
											{`: ${badgeId}`}
										</li>
									)}
									{badgeName && (
										<li>
											<span
												style={{
													fontWeight: 800,
													color: 'rgba(0, 0, 0, 0.85)',
												}}
											>
												Name
											</span>
											{`: ${badgeName}`}
										</li>
									)}
									{snappyGrantName && (
										<li>
											<span
												style={{
													fontWeight: 800,
													color: 'rgba(0, 0, 0, 0.85)',
												}}
											>
												Reward
											</span>
											{`: ${snappyGrantName}`}
										</li>
									)}
								</ul>
							</div>
						}
						getPopupContainer={(triggerNode) => triggerNode.parentNode}
					>
						<span
							style={{
								fontSize: '14px',
								color: 'var(--lochmara)',
								cursor: 'help',
							}}
						>
							Details
						</span>
					</Popover>
				);
				return withPopover;
			},
		},
		{
			title: 'Link',
			dataIndex: 'link',
			key: 'link',
			render: (link) => {
				const handleCopyLink = () => {
					navigator.clipboard.writeText(link);
					message.success('Link successfully copied to clipboard');
				};

				return (
					<div style={{ display: 'flex', alignItems: 'center' }}>
						<Popover
							content={link}
							getPopupContainer={(triggerNode) => triggerNode.parentNode}
						>
							<a href={link} target="_blank" rel="noopener noreferrer">
								External Link
							</a>
						</Popover>
						<Tooltip
							title={'Copy Link'}
							getPopupContainer={(triggerNode) => triggerNode.parentNode}
						>
							<Button
								primary="true"
								type="link"
								icon={<CopyOutlined />}
								onClick={handleCopyLink}
								style={{ margin: 0, padding: 0, color: 'var(--lochmara)' }}
							/>
						</Tooltip>
					</div>
				);
			},
		},
		{
			title: 'Gift Status',
			dataIndex: 'status',
			key: 'status',
			render: (status, record) => (
				<div style={{ display: 'flex', alignItems: 'center' }}>
					<Tag
						color={
							status === 'claimed'
								? 'green'
								: status === 'expired'
									? 'red'
									: status === 'unwrapped'
										? 'orange'
										: status === 'opened'
											? 'purple'
											: 'blue'
						}
						style={{ marginRight: 0 }}
					>
						{capitalize(status)}
					</Tag>
					{status === 'claimed' && record.tyn && (
						<Popover
							content={
								<div
									style={{ maxWidth: 200, maxHeight: 300, overflowY: 'scroll' }}
								>
									<span>{record.tyn}</span>
								</div>
							}
							getPopupContainer={(triggerNode) => triggerNode.parentNode}
						>
							<span
								style={{
									fontSize: '14px',
									color: 'var(--lochmara)',
									cursor: 'help',
									marginLeft: '8px',
								}}
							>
								Thank You Note
							</span>
						</Popover>
					)}
					{status !== 'expired' && status !== 'claimed' && (
						<Popconfirm
							title={
								<>
									Are you sure you want to expire this gift?
									<br />
									This action cannot be undone.
								</>
							}
							onConfirm={() => handleExpireGift(record.id)}
							okText="Yes"
							cancelText="No"
							getPopupContainer={(triggerNode) => triggerNode.parentNode}
						>
							<Tooltip
								title={'Expire Gift?'}
								getPopupContainer={(triggerNode) => triggerNode.parentNode}
							>
								<Button
									danger
									type="link"
									icon={<StopOutlined />}
									loading={expiringGiftId === record.id}
								/>
							</Tooltip>
						</Popconfirm>
					)}
				</div>
			),
		},
		{
			title: 'Budget',
			dataIndex: 'budgetPlan',
			key: 'budgetPlan',
			render: (budgetPlan) => {
				return <span style={{ color: 'green' }}>${budgetPlan}</span>;
			},
		},
		{
			title: 'Final Cost',
			dataIndex: 'finalCost',
			key: 'finalCost',
			render: (finalCost) => {
				if (!finalCost) return <span>–</span>;
				return (
					<div style={{ display: 'flex', alignItems: 'center' }}>
						<span style={{ color: 'green' }}>${finalCost?.totalFinalCost}</span>
						{finalCost ? (
							<Popover
								title={
									<ul
										style={{
											marginBottom: 0,
											paddingTop: 11,
											paddingBottom: 12,
										}}
									>
										<li>
											<span style={{ fontWeight: 800 }}>Cost</span>
											{`: $${finalCost?.cost}`}
										</li>
										<li>
											<span style={{ fontWeight: 800 }}>Fee</span>
											{`: $${finalCost?.finalFee}`}
										</li>
										<li>
											<span style={{ fontWeight: 800 }}>Tax</span>
											{`: $${finalCost?.finalTax}`}
										</li>
									</ul>
								}
								content={
									<div
										style={{
											maxWidth: 200,
											maxHeight: 300,
											overflowY: 'scroll',
										}}
									>
										<ul style={{ marginBottom: 0 }}>
											<li>
												<span
													style={{
														fontWeight: 800,
														color: 'rgba(0, 0, 0, 0.85)',
													}}
												>
													Total Cost
												</span>
												{`: $${finalCost?.totalFinalCost}`}
											</li>
										</ul>
									</div>
								}
								getPopupContainer={(triggerNode) => triggerNode.parentNode}
							>
								<span
									style={{
										fontSize: '14px',
										color: 'var(--lochmara)',
										cursor: 'help',
										marginLeft: '8px',
									}}
								>
									<InfoCircleOutlined />
								</span>
							</Popover>
						) : null}
					</div>
				);
			},
		},
		{
			title: 'Order Status',
			dataIndex: 'orders',
			key: 'orderStatus',
			render: (orders) => {
				const order = orders?.[0];
				if (!order) return <span>–</span>;
				const orderStatus =
					order?.orderedProducts?.[0]?.selectedProduct?.orderStatus;
				return <span>{capitalize(orderStatus)}</span>;
			},
		},
		{
			title: 'Delivery Status',
			dataIndex: 'orders',
			key: 'deliveryStatus',
			render: (orders) => {
				const order = orders?.[0];
				if (!order) return <span>–</span>;
				const isDigitalGift =
					order?.orderedProducts?.[0]?.selectedProduct?.type === 'digitalGift';
				const productTitle =
					order?.orderedProducts?.[0]?.selectedProduct?.title;
				const deliveryStatus =
					order?.orderedProducts?.[0]?.deliveryDetails?.status;
				const carrier = order?.orderedProducts?.[0]?.deliveryDetails?.carrier;
				const trackingLink =
					order?.orderedProducts?.[0]?.deliveryDetails?.trackingLink;
				const trackingNumber =
					order?.orderedProducts?.[0]?.deliveryDetails?.trackingNumber;
				const withPopover = (
					<Popover
						content={
							<div
								style={{
									maxWidth: 200,
									maxHeight: 300,
									overflowY: 'scroll',
									marginBottom: 0,
								}}
							>
								{isDigitalGift ? (
									<ul style={{ marginBottom: 0 }}>
										<li>
											<span
												style={{
													fontWeight: 800,
													color: 'rgba(0, 0, 0, 0.85)',
												}}
											>
												Method
											</span>
											{`: Digital`}
										</li>
										{productTitle && (
											<li>
												<span
													style={{
														fontWeight: 800,
														color: 'rgba(0, 0, 0, 0.85)',
													}}
												>
													Product
												</span>
												{`: ${productTitle}`}
											</li>
										)}
									</ul>
								) : (
									<ul style={{ marginBottom: 0 }}>
										{carrier && (
											<li>
												<span
													style={{
														fontWeight: 800,
														color: 'rgba(0, 0, 0, 0.85)',
													}}
												>
													Carrier
												</span>
												{`: ${carrier}`}
											</li>
										)}
										{trackingNumber && (
											<li>
												<span
													style={{
														fontWeight: 800,
														color: 'rgba(0, 0, 0, 0.85)',
													}}
												>
													Tracking #
												</span>
												{`: ${trackingNumber}`}
											</li>
										)}
										{trackingLink && (
											<li>
												<span
													style={{
														fontWeight: 800,
														color: 'rgba(0, 0, 0, 0.85)',
													}}
												>
													Tracking Link
												</span>
												{`: `}
												<a
													href={trackingLink}
													target="_blank"
													rel="noopener noreferrer"
												>
													Carrier Site
												</a>
											</li>
										)}
										{productTitle && (
											<li>
												<span
													style={{
														fontWeight: 800,
														color: 'rgba(0, 0, 0, 0.85)',
													}}
												>
													Product
												</span>
												{`: ${productTitle}`}
											</li>
										)}
									</ul>
								)}
							</div>
						}
						getPopupContainer={(triggerNode) => triggerNode.parentNode}
					>
						<span
							style={{
								fontSize: '14px',
								color: 'var(--lochmara)',
								cursor: 'help',
							}}
						>
							{isDigitalGift
								? 'Delivered'
								: deliveryStatus === 'inTransit'
									? 'In Transit'
									: capitalize(deliveryStatus)}
						</span>
					</Popover>
				);
				return withPopover;
			},
		},
	];

	return (
		<div>
			<Title
				level={2}
				style={{
					margin: 0,
					fontWeight: 700,
					fontSize: '30px',
					marginBottom: 12,
				}}
			>
				Snappy History
			</Title>

			<div style={{ marginBottom: 16 }}>
				<div style={{ marginBottom: 12 }}>
					<Switch
						checked={showAllCampaigns}
						onChange={handleShowAllCampaignsToggle}
					/>
					<span style={{ marginLeft: 8 }}>Show All Campaigns</span>
				</div>

				{!showAllCampaigns && (
					<div style={{ marginBottom: 12 }}>
						<label style={{ display: 'block', marginBottom: 4 }}>
							Select Campaigns:
						</label>
						<Select
							mode="multiple"
							placeholder="Select campaigns"
							value={selectedCampaigns}
							onChange={handleCampaignChange}
							options={campaignOptions}
							style={{ width: '100%', maxWidth: 600 }}
							maxTagCount="responsive"
						/>
					</div>
				)}

				<div>
					<label style={{ display: 'block', marginBottom: 4 }}>
						Specify Date Range:
					</label>
					<RangePicker
						value={dateRange}
						onChange={handleDateRangeChange}
						format="YYYY-MM-DD"
						placeholder={['Start Date', 'End Date']}
						style={{ width: '100%', maxWidth: 400 }}
					/>
				</div>
			</div>

			{initialLoading ? (
				<div style={{ textAlign: 'center', padding: '50px' }}>
					<Spin size="large" />
				</div>
			) : (
				<>
					<motion.div
						initial={{ opacity: 0, x: 20 }}
						animate={{ opacity: 1, x: 0 }}
						exit={{ opacity: 0, x: -20 }}
						transition={{ duration: 0.3, ease: 'easeOut' }}
					>
						<Table
							dataSource={snappyGiftData}
							columns={snappyHistoryColumns}
							rowKey="id"
							showSorterTooltip={false}
							style={{ overflow: 'visible' }}
							loading={
								loadingMore
									? {
											indicator: (
												<div
													style={{
														display: 'flex',
														justifyContent: 'center',
														alignItems: 'center',
														minHeight: '300px',
													}}
												>
													<Spin size="large" />
												</div>
											),
										}
									: false
							}
							pagination={{
								pageSize: pageSize,
								current: displayPageIndex + 1,
								total: hasNextPage
									? Math.min(knownTotalPages * pageSize, snappyGiftData.length)
									: snappyGiftData.length,
								onChange: (page) => {
									const newPageIndex = page - 1;
									if (fetchedPages.has(newPageIndex)) {
										setDisplayPageIndex(newPageIndex);
									}
									setPageIndex(newPageIndex);
								},
								showSizeChanger: false,
							}}
						/>
					</motion.div>
				</>
			)}
		</div>
	);
};

export default SnappyHistoryComponent;
